import { DUE_TIME_STEP_MINUTES, dueTimeOf, snapDueTime } from '@nedelnik/shared';

import { ArrowUpRight, Calendar, Times } from '../icons.js';

import { googleCalendarUrl } from './calendar.js';
import { dueLabel, dueTone } from './due.js';

import type { Task } from '@nedelnik/shared';

/**
 * Срок задачи: метка снаружи и поле в панели.
 *
 * Цвета не настраиваются — правило одинаковое для всех задач (3.6.5), поэтому
 * тон приходит из `dueState`, а не из свойств компонента.
 */

/**
 * Светофор снаружи: близость срока видна без открытия задачи (3.6.3).
 *
 * Значок календаря плюс короткая подпись, а не одна подпись: в строке отметок
 * рядом стоят замок, зонтик и чек-лист, и слово без значка выпадало из ряда.
 * Сама подпись остаётся («сегодня», «28 авг.») — она отвечает на вопрос
 * точнее цвета, а цвет виден боковым зрением.
 */
export function DueChip({ task, today }: { task: Task; today: string }): React.ReactElement | null {
  if (task.due === null) return null;

  const tone = dueTone(task, today);
  const time = dueTimeOf(task);
  return (
    <span
      className={`mark due due--${tone}`}
      title={time === null ? `Срок: ${task.due}` : `Срок: ${task.due} ${time}`}
    >
      <Calendar />
      {dueLabel(task, today)}
    </span>
  );
}

/**
 * Поле срока в панели: дата, необязательное время (3.6.2) и снятие срока —
 * задача без срока нормальна (3.6.1).
 *
 * В строке остались только поле и крестик. Быстрый вариант «конец недели» и
 * значок состояния убраны, и обе потери небольшие: дата всё равно ставится
 * календарём, а насколько срок близок, видно снаружи по отметке (3.6.3) —
 * в открытой карточке человек и так смотрит на саму дату. Втроём же они не
 * помещались в строку и переносили крестик на второй ряд.
 *
 * Состояние переехало в подпись поля: она уже занимает свою строку, и слово
 * там не стоит ни пикселя высоты. Слово, а не цвет: цвет один не различают
 * при дальтонизме, а подпись читается и вслух.
 */
/**
 * Нажатие по пустому полю открывает календарь.
 *
 * Сам браузер открывает его по значку внутри поля — и до этого значка у
 * пустого поля не добраться. В Safari поверх поля лежит наша заглушка «дата»
 * (см. стили): нажатия она пропускает, но значок под ней закрывает; в Chrome
 * значка у поля со снятым родным оформлением нет вовсе. В обоих случаях
 * нажатие по пустому полю не делало ничего, и поставить срок мышью было
 * нечем (замечание владельца, 2026-09-13). Убрать заглушку нельзя — без неё
 * пустое поле в Safari просто пустой прямоугольник.
 *
 * Только с мышью и только у пустого поля. На телефоне календарь и так
 * открывается касанием в любое место поля, и второй вызов там лишний; у
 * заполненного поля нажатие попадает в число, которое правят с клавиатуры, и
 * подменять эту правку календарём — уже не починка, а другое поведение.
 */
function openPicker(input: HTMLInputElement): void {
  if (input.value !== '') return;
  if (!(globalThis.matchMedia?.('(hover: hover)').matches ?? false)) return;

  try {
    input.showPicker();
  } catch {
    // Браузер постарше или вызов, который он не счёл жестом человека. Поле
    // остаётся при родном поведении — то есть при том, что было до правки.
  }
}

/**
 * Время рядом с датой (3.6.2, этап 21 плана).
 *
 * Поле времени показывается только у заполненной даты: время без даты
 * бессмысленно, и интерфейс повторяет это правило сам, а не подписью. Крестик
 * снимает срок целиком — дату и время одной правкой, иначе на двух устройствах
 * время способно пережить дату (см. `setDue`).
 */
export function DueField({
  task,
  today,
  onChange,
}: {
  task: Task;
  today: string;
  onChange: (due: string | null, dueTime: string | null) => void;
}): React.ReactElement {
  const tone = dueTone(task, today);
  const state = task.due !== null && tone !== 'far' ? dueLabel(task, today) : null;
  const time = dueTimeOf(task);
  // Ссылка только у срока со временем: в календарь просятся встречи, а не
  // «сделать до». Срок без времени стал бы событием на весь день, которое
  // ничего не назначает, и ссылка у большинства задач была бы шумом.
  const google = time === null ? null : googleCalendarUrl(task);

  return (
    <div className="field">
      {/*
       * Подпись — обёртка, а не сам `label`: в строке рядом стоит ссылка на
       * Google, а ссылка внутри `label` перехватывала бы нажатие на поле.
       * `htmlFor` остаётся у слова «Дедлайн», и озвучка называет поле как прежде.
       */}
      <div className="field__label">
        <label htmlFor={`due-${task.id}`}>Дедлайн</label>
        {state !== null && (
          <span className={`field__state field__state--${tone === 'overdue' ? 'danger' : 'warn'}`}>
            {state}
          </span>
        )}
        {google !== null && (
          /*
           * В Google Календарь (3.6.7, этап 22): ссылка на форму события с
           * заполненными полями, в новой вкладке. Мелко и в строке подписи, а
           * не кнопкой под полем: действие нужно раз на задачу, и две крупные
           * плашки отнимали у карточки место (замечание владельца, 2026-09-19).
           * В одну сторону: правка срока в календарь не уезжает.
           */
          <a
            className="due__google"
            href={google}
            target="_blank"
            rel="noopener noreferrer"
            title="Открыть форму события в Google Календаре"
          >
            <ArrowUpRight />В Google Календарь
          </a>
        )}
      </div>
      <div className="field__row">
        <input
          id={`due-${task.id}`}
          type="date"
          value={task.due ?? ''}
          /* Пустоту поля видит только React: см. заглушку в стилях. */
          data-empty={task.due === null ? 'true' : 'false'}
          onClick={(event) => openPicker(event.currentTarget)}
          onChange={(event) =>
            onChange(event.target.value === '' ? null : event.target.value, time)
          }
        />
        {task.due !== null && (
          <span className="due__time">
            <input
              type="time"
              aria-label="Время"
              value={time ?? ''}
              /*
               * Шаг в пять минут — и подборщику, и записи. `step` соблюдают
               * Chrome и Android, колесо iOS предлагает каждую минуту, поэтому
               * выбранное ещё и округляется (`snapDueTime`): в записи 10:40
               * или 10:45, а не 10:42, откуда бы оно ни пришло.
               */
              step={DUE_TIME_STEP_MINUTES * 60}
              data-empty={time === null ? 'true' : 'false'}
              onClick={(event) => openPicker(event.currentTarget)}
              onChange={(event) =>
                onChange(
                  task.due,
                  event.target.value === '' ? null : snapDueTime(event.target.value),
                )
              }
            />
            {time !== null && (
              /*
               * Снять одно время, оставив дату. Без этого крестика время
               * убиралось только вместе с датой (замечание владельца,
               * 2026-09-19), а колесо iOS «пусто» не предлагает. Крестик
               * внутри поля, у правого края: отдельной кнопкой в строке он
               * вставал вплотную к красному соседу, и два одинаковых × рядом
               * путали, а дата на телефоне сжималась до «09/19/».
               */
              <button
                type="button"
                className="due__timeclear"
                aria-label="Убрать время"
                title="Убрать время"
                onClick={() => onChange(task.due, null)}
              >
                <Times />
              </button>
            )}
          </span>
        )}
        {task.due !== null && (
          <button
            type="button"
            className="iconbtn iconbtn--danger"
            aria-label="Убрать срок"
            title="Убрать срок"
            onClick={() => onChange(null, null)}
          >
            <Times />
          </button>
        )}
      </div>
    </div>
  );
}
